import { ISbAlternateObject } from 'storyblok-js-client';
import { MetadataRoute } from 'next';

import { createSitemapItem, getFlatStories } from '@monoimin/sitemap';
import { replaceMultipleTexts } from '@monoimin/utils/formatters';

import { GET_ALL_TERRITORIES_PAGINATION } from '../constants';
import { GetStrapiEstatesResponse } from '../types';

import { getStrapiCountries } from './getStrapiCountries';
import { getAllEstates } from './getAllEstates';
import { getStrapiEstates } from './getStrapiEstates';
import { getPropertyTypes } from './getPropertyTypes';

import type { GetEstatesFilterDataParams, Locales } from '@monoimin/sb-types';

export async function getEstatesPages(
  locale: Locales
): Promise<MetadataRoute.Sitemap> {
  const result = await getAllEstates({ page: 1 }, locale);
  if (!result.estates || !result.pageCount) return [];

  const requests: Promise<GetStrapiEstatesResponse>[] = [];
  for (let i = 2; i <= (result.pageCount || 2); i++) {
    const request = getAllEstates({ page: i }, locale);
    requests.push(request);
  }

  const responses = await Promise.all(requests);

  const allEstates = [
    result.estates,
    ...responses.map((el) => {
      return el.estates;
    }),
  ].flat();

  const allEstatePages: MetadataRoute.Sitemap = [];
  for (const el of allEstates) {
    if (!el.country_slug) continue;
    const countrySlug = replaceMultipleTexts(el.country_slug || '', {
      'united-arab-emirates': 'uae',
    });
    allEstatePages.push(
      createSitemapItem(
        `/real-estate/${countrySlug}/${el.slug}`,
        el?.publishedAt,
        locale
      )
    );
  }

  return allEstatePages;
}

export async function getEstatesCountriesWithTypesPages(
  locale: Locales
): Promise<MetadataRoute.Sitemap> {
  const { countries } = await getStrapiCountries({
    locale: locale,
    pagination: GET_ALL_TERRITORIES_PAGINATION,
  });
  const sitemapItems: MetadataRoute.Sitemap = [];
  for (const country of countries) {
    const pass = country.slug && country.code;
    if (!pass) {
      continue;
    }
    const countrySlug = replaceMultipleTexts(country.slug || '', {
      'united-arab-emirates': 'uae',
    });
    const countryHref = `/real-estate/${countrySlug}`;
    sitemapItems.push(
      createSitemapItem(countryHref, country.publishedAt, locale)
    );

    const countryObjectTypes = await getPropertyTypes(
      { locale },
      country.code,
      country.slug
    );

    countryObjectTypes.forEach((item) => {
      sitemapItems.push(
        createSitemapItem(
          `${countryHref}/${item.slug}`,
          country.publishedAt,
          locale
        )
      );
    });
  }

  return sitemapItems;
}

export async function getEstatesCountriesPages(
  locale: Locales
): Promise<MetadataRoute.Sitemap> {
  const { countries } = await getStrapiCountries({
    locale: locale,
    pagination: GET_ALL_TERRITORIES_PAGINATION,
  });

  const sitemapItems: MetadataRoute.Sitemap = [];
  for (const country of countries) {
    const pass = country.slug && country.code;
    if (!pass) {
      continue;
    }
    const countrySlug = replaceMultipleTexts(country.slug || '', {
      'united-arab-emirates': 'uae',
    });
    sitemapItems.push(
      createSitemapItem(
        `/real-estate/${countrySlug}`,
        country.publishedAt,
        locale
      )
    );
  }

  return sitemapItems;
}

export async function getEstatesObjectTypePages<L extends Locales>(
  locale: L
): Promise<MetadataRoute.Sitemap> {
  const objectTypes = await getPropertyTypes({ locale });
  const results: MetadataRoute.Sitemap = [];
  objectTypes.forEach(async (item) => {
    const getEstatesParams: GetEstatesFilterDataParams = {
      page: 1,
      locale,
      pageSize: 1,
      sort: 'updatedAt:desc',
      objectType: [...item.filtersState.object_type],
      fields: ['publishedAt'],
    };
    const currentEstate = await getStrapiEstates(getEstatesParams);
    results.push(
      createSitemapItem(
        `/real-estate/${item.slug}`,
        currentEstate.estates?.[0].publishedAt,
        locale
      )
    );
  });
  return results;
}

export async function getEstatesStaticPages(
  locale: Locales
): Promise<MetadataRoute.Sitemap> {
  const getEstatesCommonParams: GetEstatesFilterDataParams = {
    page: 1,
    locale,
    pageSize: 1,
    sort: 'updatedAt:desc',
  };

  const estate = await getStrapiEstates(getEstatesCommonParams);
  const homePage = createSitemapItem(
    `/real-estate/`,
    estate.estates?.[0].publishedAt,
    locale
  );

  const objectStageParams: GetEstatesFilterDataParams = {
    objectStage: ['under-construction'],
    ...getEstatesCommonParams,
  };
  const estateByStage = await getStrapiEstates(objectStageParams);
  const underConstriction = createSitemapItem(
    `/real-estate/under-construction/`,
    estateByStage.estates?.[0].publishedAt,
    locale
  );

  const search = createSitemapItem(
    `/real-estate/search/`,
    estate.estates?.[0].publishedAt,
    locale
  );
  return [homePage, underConstriction, search];
}

export async function getGuidesPages(
  locale: Locales
): Promise<MetadataRoute.Sitemap> {
  const links = await getFlatStories<Array<ISbAlternateObject>>({
    version: 'published',
    sort_by: 'first_published_at:desc',
    starts_with: `immigrantinvest/${locale}/real-estate-guides`,
    filter_query: {
      component: {
        in: 'realEstateGuide',
      },
    },
  });

  const pages = links.map((story) =>
    createSitemapItem(
      `real-estate/guides/${story.slug}`,
      story.first_published_at,
      locale
    )
  );
  const guidesHome = createSitemapItem(
    `real-estate/guides/`,
    links?.[0].first_published_at,
    locale
  );
  return [...pages, guidesHome];
}
